import React, { useCallback } from 'react';
import {
  TabBar as BaseTabBar,
  TabView as BaseTabView,
  NavigationState,
  Route,
  SceneRendererProps,
  TabBarItem,
  TabDescriptor,
  TabViewProps,
} from 'react-native-tab-view';
import { createStyles } from '@/contexts/theme-context';
import { shadow } from '@/styles';
import { StyleProp, ViewStyle } from 'react-native';

export type Props<T extends Route> = TabViewProps<T> & {
  indicatorStyle?: StyleProp<ViewStyle>;
  label: TabDescriptor<T>['label'];
  scrollEnabled?: boolean;
  tabBarTabStyle?: StyleProp<ViewStyle>;
};

export const TabView = <T extends Route>({
  indicatorStyle,
  label,
  tabBarTabStyle,
  style,
  ...rest
}: Props<T>): React.ReactElement => {
  const [styles, theme] = useStyles();

  const renderTabBar: TabViewProps<T>['renderTabBar'] = useCallback(
    (props: SceneRendererProps & { navigationState: NavigationState<T> }) => {
      return (
        <BaseTabBar
          {...props}
          activeColor={theme.colors.primary}
          inactiveColor={theme.colors.inactiveText}
          indicatorStyle={[styles.tabBarIndicator, indicatorStyle]}
          pressOpacity={0.8}
          renderTabBarItem={(tabBarItemProps) => (
            <TabBarItem
              {...tabBarItemProps}
              key={tabBarItemProps.key}
              label={label}
            />
          )}
          scrollEnabled={rest.scrollEnabled}
          style={[styles.tabBar, style]}
          tabStyle={[styles.tabBarTab, tabBarTabStyle]}
        />
      );
    },
    [
      indicatorStyle,
      label,
      style,
      styles,
      tabBarTabStyle,
      theme,
      rest.scrollEnabled,
    ],
  );

  return (
    <BaseTabView
      renderTabBar={renderTabBar}
      {...rest}
      // Disabled to prevent interference with app tab swiping
      swipeEnabled={false}
    />
  );
};

const useStyles = createStyles((theme) => ({
  tabBar: {
    ...shadow({
      color: theme.colors.black,
      elevation: 2,
      opacity: 0.1,
      radius: 4,
      x: 0,
      y: 4,
    }),
    backgroundColor: theme.colors.white,
  },
  tabBarIndicator: {
    backgroundColor: theme.colors.accent,
    height: 4,
  },
  tabBarTab: {
    minHeight: 0,
    paddingVertical: 16,
  },
}));
